/**
 * Score grading + combo state machine.
 *
 * Grade is decided by (correctness, time taken, mistake count):
 *   PERFECT — first try, no errors, within "fast" window
 *   GREAT   — first try, no errors, slower than fast window
 *   GOOD    — correct eventually but used a hint or made a typo
 *   MISS    — wrong / abandoned
 *
 * Combo state holds the player's current streak. Every PERFECT/GREAT/GOOD
 * extends it; MISS resets to 0. Multiplier scales with streak length, used
 * to boost XP rewards.
 */

/**
 * Speech recognition score thresholds (used by SpeakingPage to map a 0-100
 * pronunciation similarity into Grade tiers). Single source of truth.
 */
export const SPEAKING_THRESHOLDS = {
  perfect: 90,
  great: 80,
  good: 60,
} as const;

/** Map a 0-100 speaking score into a Grade. */
export function speakingGrade(score: number): 'perfect' | 'great' | 'good' | 'miss' {
  if (score >= SPEAKING_THRESHOLDS.perfect) return 'perfect';
  if (score >= SPEAKING_THRESHOLDS.great) return 'great';
  if (score >= SPEAKING_THRESHOLDS.good) return 'good';
  return 'miss';
}

/** AI quota caps per plan, per calendar day. */
export const AI_QUOTA_PER_DAY = { free: 30, pro: 300 } as const;

/** Mastery tier count — sentences progress L0 → L3. */
export const MAX_MASTERY = 3;

export type Grade = 'perfect' | 'great' | 'good' | 'miss';

export interface GradeMeta {
  label: string;        // "PERFECT"
  zh: string;           // "完美！"
  color: string;        // tailwind text class
  bgColor: string;      // glow gradient
  xp: number;           // base XP awarded
  emoji: string;
  ringColor: string;    // burst ring color
}

export const GRADE_META: Record<Grade, GradeMeta> = {
  perfect: {
    label: 'PERFECT',
    zh: '完美！',
    color: 'text-honey-400',
    bgColor: 'from-honey-400/30 to-honey-500/0',
    xp: 30,
    emoji: '✨',
    ringColor: '#00f0ff',
  },
  great: {
    label: 'GREAT',
    zh: '不错！',
    color: 'text-mint-400',
    bgColor: 'from-mint-400/30 to-mint-500/0',
    xp: 18,
    emoji: '⚡',
    ringColor: '#aaff00',
  },
  good: {
    label: 'GOOD',
    zh: '通过',
    color: 'text-cream-200',
    bgColor: 'from-cream-200/20 to-cream-200/0',
    xp: 8,
    emoji: '👌',
    ringColor: '#d8d8e8',
  },
  miss: {
    label: 'MISS',
    zh: '断了',
    color: 'text-ember-400',
    bgColor: 'from-ember-400/30 to-ember-500/0',
    xp: 0,
    emoji: '💢',
    ringColor: '#ff2e88',
  },
};

interface GradeInput {
  correct: boolean;
  elapsedMs: number;
  hintUsed: boolean;
  errorCount: number;
  /** Fast threshold in ms; defaults vary by mode (puzzle 8s, typing 12s, speaking 6s). */
  fastThresholdMs?: number;
}

export function grade({
  correct,
  elapsedMs,
  hintUsed,
  errorCount,
  fastThresholdMs = 8000,
}: GradeInput): Grade {
  if (!correct) return 'miss';
  if (hintUsed || errorCount > 0) return 'good';
  if (elapsedMs <= fastThresholdMs) return 'perfect';
  if (elapsedMs <= fastThresholdMs * 2) return 'great';
  return 'good';
}

/** Multiplier — how much XP combo boosts the base reward. */
export function multiplier(combo: number): number {
  if (combo >= 16) return 5;
  if (combo >= 8) return 3;
  if (combo >= 4) return 2;
  return 1;
}

export function multiplierLabel(m: number): string {
  if (m === 5) return '×5 GODLIKE';
  if (m === 3) return '×3 ON FIRE';
  if (m === 2) return '×2 STREAK';
  return '';
}

/** XP earned for an attempt — base × combo multiplier. */
export function xpFor(g: Grade, combo: number): number {
  return GRADE_META[g].xp * multiplier(combo);
}
